import React from 'react';
import {
  Pressable,
  StyleSheet,
  ViewStyle,
  Platform,
  StyleProp,
  View,
} from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../hooks/useTheme';

export interface FloatingActionButtonProps {
  onPress: () => void;
  /** When true, shows a close icon (e.g. modal is open). */
  active?: boolean;
  /** Distance from bottom of the positioning context (use safe area + nav in parent). */
  bottom: number;
  /** Distance from right edge. */
  right: number;
  /** Circle diameter. */
  size?: number;
  /** Optional override for FAB background (defaults to theme primary). */
  backgroundColor?: string;
  /** Optional style for the absolutely positioned wrapper. */
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  testID?: string;
  /**
   * When true (default on web), uses viewport-fixed positioning so the FAB
   * stays bottom-right while scrolling. Set false if you embed inside a Modal.
   * On web, the button is rendered via a portal to `document.body` so it is not
   * trapped by React Navigation / transformed ancestors (which break CSS `fixed`).
   */
  viewportFixed?: boolean;
}

/**
 * Circular floating action button (default: robot icon; active: close).
 * Parent should position via `bottom` / `right` from safe area and tab bar.
 * On web, defaults to `position: fixed` + portal to `document.body` so the button
 * stays in the viewport corner while scrolling on desktop.
 */
const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  onPress,
  active = false,
  bottom,
  right,
  size = 56,
  backgroundColor,
  style,
  accessibilityLabel,
  testID,
  viewportFixed = Platform.OS === 'web',
}) => {
  const { colors } = useTheme();
  const bg = backgroundColor ?? colors.primary;
  const topBevel = 'rgba(255,255,255,0.35)';
  const bottomBevel = 'rgba(0,0,0,0.28)';

  const position: ViewStyle =
    viewportFixed && Platform.OS === 'web'
      ? ({
          position: 'fixed',
          bottom,
          right,
        } as unknown as ViewStyle)
      : {
          position: 'absolute',
          bottom,
          right,
        };

  const pressable = (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={
        accessibilityLabel ??
        (active ? 'Close AI insights' : 'Open AI color insights')
      }
      onPress={onPress}
      style={({ pressed }) => [
        styles.fab,
        position,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: bg,
          borderWidth: 1.2,
          borderTopColor: topBevel,
          borderLeftColor: topBevel,
          borderRightColor: bottomBevel,
          borderBottomColor: bottomBevel,
          // Above app chrome / drawers on web when portaled to body
          ...(viewportFixed && Platform.OS === 'web'
            ? ({ zIndex: 99999 } as ViewStyle)
            : {}),
          transform: [{ translateY: pressed ? 2 : 0 }, { scale: pressed ? 0.98 : 1 }],
          ...Platform.select({
            ios: {
              shadowColor: '#000',
              shadowOffset: { width: 0, height: pressed ? 2 : 6 },
              shadowOpacity: pressed ? 0.2 : 0.32,
              shadowRadius: pressed ? 4 : 10,
            },
            android: { elevation: pressed ? 4 : 10 },
            web: {
              boxShadow: pressed
                ? '0 4px 8px rgba(0,0,0,0.28)'
                : '0 10px 20px rgba(0,0,0,0.28)',
            },
            default: {},
          }),
        },
        style,
      ]}
    >
      {active ? (
        <Ionicons name="close" size={28} color={colors.background} />
      ) : (
        <MaterialCommunityIcons name="robot" size={28} color={colors.background} />
      )}
    </Pressable>
  );

  if (
    viewportFixed &&
    Platform.OS === 'web' &&
    typeof document !== 'undefined'
  ) {
    // Portal escapes React Navigation / transformed parents (fixes desktop scroll).
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { createPortal } = require('react-dom') as {
      createPortal: (
        child: React.ReactNode,
        container: Element | DocumentFragment
      ) => React.ReactNode;
    };
    return createPortal(pressable, document.body) as React.ReactElement;
  }

  return pressable;
};

/** Full-screen overlay so a non–viewport-fixed FAB sits above scroll (native / non-web). */
export const FloatingActionButtonOverlay: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <View pointerEvents="box-none" style={styles.overlay}>
    {children}
  </View>
);

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 10000,
  },
  fab: {
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
  },
});

export default FloatingActionButton;
